import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { approvePublish, rejectPublish, cancelPublishRequest, fetchSFQDetail } from "../Redux/feasibility";
import { ContentModal } from "./VersionContent";
import { DiffModal } from "./VersionDiff";
import { sfqApi } from "../../../api/feasibility";

// Sits directly under the StatusBar on every tab, so a PM who lands on Sites still
// sees that a publish is waiting on them.
const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 14px;
  font-size: 12.5px;
  background: ${(p) => (p.$rejected ? "#fff5f5" : "#e7f1ff")};
  border: 1px solid ${(p) => (p.$rejected ? "#f1aeb5" : "#9ec5fe")};
  color: ${(p) => (p.$rejected ? "#842029" : "#084298")};
`;

const ActionBtn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.$color || "#0d6efd"};
  background: ${(p) => (p.$filled ? (p.$color || "#0d6efd") : "#fff")};
  color: ${(p) => (p.$filled ? "#fff" : (p.$color || "#0d6efd"))};
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.92); }
`;

const Spacer = styled.span`
  margin-left: auto;
`;

const ReasonBox = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background: #fff;
`;

const ReasonLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #dc3545;
`;

const ReasonInput = styled.textarea`
  width: 100%;
  min-height: 64px;
  resize: vertical;
  padding: 6px 8px;
  font-size: 12px;
  font-family: inherit;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;

const ReasonFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CharCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: ${(p) => (p.$over ? "#dc3545" : "#adb5bd")};
`;

const NoticeTitle = styled.strong`
  display: block;
  margin-bottom: 3px;
`;

const RejectReason = styled.div`
  flex-basis: 100%;
  padding: 8px 10px;
  border-left: 3px solid #dc3545;
  border-radius: 3px;
  background: #fff;
  white-space: pre-wrap;
`;

const REASON_MAX = 1000;

export default function PublishApprovalBar({ sfq, isOwner, isReviewer }) {
  const dispatch = useDispatch();
  // The frozen working copy — what approval would publish. The tabs show the
  // version currently in force instead, so the change is only visible here.
  const workingQuestions = useSelector((s) => s.feasibility.sfqQuestions);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [diff, setDiff] = useState(null);

  const pending = !!sfq.publishPendingPM;
  const nextVersionNo = (sfq.latestVersionNo ?? 0) + 1;
  const trimmedReason = reason.trim();
  const reasonInvalid = !trimmedReason || trimmedReason.length > REASON_MAX;

  async function handleApprove() {
    if (!window.confirm(
      "현재 작업본을 새 버전으로 발행합니다.\n" +
      "이후 발송분부터 적용되며, 이미 발송된 사이트는 받은 버전을 유지합니다.\n계속하시겠습니까?"
    )) return;
    setBusy(true);
    try {
      const res = await dispatch(approvePublish(sfq.id)).unwrap();
      // Published, but the requester notification email may have failed to send.
      alert(res?.emailWarning
        ? `새 버전(V${res.versionNo})이 발행되었습니다.\n${res.emailWarning}`
        : `새 버전(V${res.versionNo})이 발행되었습니다.`);
    } catch (e) {
      alert(e?.message || "발행 승인에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (reasonInvalid) return;
    setBusy(true);
    try {
      const res = await dispatch(rejectPublish({ id: sfq.id, reason: trimmedReason })).unwrap();
      // Rejected, but the owner notification email may have failed to send.
      if (res?.emailWarning) alert(res.emailWarning);
      setRejectOpen(false);
      setReason("");
    } catch (e) {
      alert(e?.message || "반려에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (!window.confirm("발행 승인 요청을 취소하고 다시 편집하시겠습니까?")) return;
    setBusy(true);
    try {
      await dispatch(cancelPublishRequest(sfq.id)).unwrap();
      // The working copy is editable again; reload it so the editors are in sync.
      await dispatch(fetchSFQDetail(sfq.id));
    } catch (e) {
      alert(e?.message || "요청 취소에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  // Not waiting on anyone: the owner still needs to see why their last request failed.
  if (!pending) {
    if (!isOwner || !sfq.lastPublishRejectComment || !sfq.contentDirty) return null;
    return (
      <Wrap $rejected>
        <NoticeTitle>Reviewer가 발행 요청을 반려했습니다.</NoticeTitle>
        <RejectReason>{sfq.lastPublishRejectComment}</RejectReason>
        <span>편집 중이던 내용은 그대로 남아 있습니다. 수정한 뒤 다시 승인을 요청해 주세요.</span>
      </Wrap>
    );
  }

  // The working copy is frozen while a request is pending, so the server-side diff of
  // it against the latest version is exactly what approving would apply.
  async function openDiff() {
    setDiff({ loading: true, data: null, error: null });
    try {
      const res = await sfqApi.getWorkingDiff(sfq.id);
      setDiff({ loading: false, data: res.data.data, error: null });
    } catch (e) {
      setDiff({
        loading: false,
        data: null,
        error: e?.response?.data?.error?.message || e?.message ||
               "변경 내역을 불러오지 못했습니다.",
      });
    }
  }

  // Opens the proposed change; the tabs behind it show the version in force.
  // `변경본 보기` is the full working copy, `변경 내역` only what differs from V(n).
  const viewer = (
    <>
      <ActionBtn $color="#0d6efd" onClick={() => setViewOpen(true)}>변경본 보기</ActionBtn>
      <ActionBtn $color="#0d6efd" onClick={openDiff}>변경 내역</ActionBtn>
      {diff && (
        <DiffModal
          title={
            sfq.latestVersionNo != null
              ? `V${sfq.latestVersionNo} → V${nextVersionNo} 변경 내역`
              : `첫 버전(V${nextVersionNo}) 발행 — 전체 신규`
          }
          subtitle={
            sfq.latestVersionNo != null
              ? `현재 적용 중인 V${sfq.latestVersionNo} 대비 달라지는 부분입니다. 승인하면 그대로 V${nextVersionNo}으로 발행됩니다.`
              : `비교할 이전 버전이 없어 전체가 신규 내용입니다.`
          }
          diff={diff.data}
          loading={diff.loading}
          error={diff.error}
          onClose={() => setDiff(null)}
        />
      )}
      {viewOpen && (
        <ContentModal
          title={`발행 대기 중인 변경본 (승인 시 V${nextVersionNo})`}
          subtitle={
            sfq.latestVersionNo != null
              ? `현재 적용 중인 버전은 V${sfq.latestVersionNo}입니다. 승인하면 아래 내용이 V${nextVersionNo}으로 발행됩니다.`
              : `승인하면 아래 내용이 첫 버전(V${nextVersionNo})으로 발행됩니다.`
          }
          narrativeHtml={sfq.narrativeHtml}
          questions={workingQuestions}
          onClose={() => setViewOpen(false)}
        />
      )}
    </>
  );

  if (isReviewer) {
    return (
      <Wrap>
        <span>
          <b>발행 승인 대기</b> — Owner가 새 버전 발행 승인을 요청했습니다.
          <b> 변경본 보기</b>로 내용을 확인한 뒤 승인해 주세요.
        </span>
        <Spacer />
        {viewer}
        <ActionBtn $filled $color="#198754" disabled={busy || rejectOpen} onClick={handleApprove}>
          {busy && !rejectOpen ? "처리 중…" : "발행 승인"}
        </ActionBtn>
        <ActionBtn $color="#dc3545" disabled={busy || rejectOpen} onClick={() => setRejectOpen(true)}>
          반려
        </ActionBtn>

        {rejectOpen && (
          <ReasonBox>
            <ReasonLabel htmlFor="publishRejectReason">
              반려 사유 (필수) — 요청한 Owner에게 전달되며 감사 추적에 기록됩니다.
            </ReasonLabel>
            <ReasonInput
              id="publishRejectReason"
              autoFocus
              value={reason}
              disabled={busy}
              placeholder="어떤 부분을 수정해야 하는지 구체적으로 적어주세요."
              onChange={(e) => setReason(e.target.value)}
            />
            <ReasonFoot>
              <ActionBtn $filled $color="#dc3545" disabled={busy || reasonInvalid} onClick={handleReject}>
                {busy ? "처리 중…" : "반려하기"}
              </ActionBtn>
              <ActionBtn $color="#6c757d" disabled={busy}
                onClick={() => { setRejectOpen(false); setReason(""); }}>
                취소
              </ActionBtn>
              <CharCount $over={trimmedReason.length > REASON_MAX}>
                {trimmedReason.length} / {REASON_MAX}
              </CharCount>
            </ReasonFoot>
          </ReasonBox>
        )}
      </Wrap>
    );
  }

  return (
    <Wrap>
      <span>
        <b>Reviewer 승인 대기 중</b>입니다. 아래 탭에는 현재 적용 중인 버전이 표시되며,
        승인 전까지 내용은 <b>편집할 수 없습니다</b>.
      </span>
      <Spacer />
      {viewer}
      {isOwner && (
        <ActionBtn $color="#6c757d" disabled={busy} onClick={handleCancel}>
          {busy ? "취소 중…" : "요청 취소"}
        </ActionBtn>
      )}
    </Wrap>
  );
}
